import React, { useState, useEffect } from 'react';
import {
  Table, Tag, Button, Space, Modal, Upload, message,
  List, Progress, Card, Checkbox, Empty, Image, Switch, Form, Input
} from 'antd';
import {
  UploadOutlined, InboxOutlined, CheckCircleOutlined,
  WarningOutlined, ExclamationCircleOutlined, PaperClipOutlined,
  SaveOutlined, FileImageOutlined, FilePdfOutlined, EyeOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import axios from 'axios';
import AuthService from '../../utils/auth';
import dayjs from 'dayjs';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

const StepAttachmentUpload = ({ tourists, tour, tourNotice, completeness, onRefresh }) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [files, setFiles] = useState([]);
  const [matchName, setMatchName] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(0);
  const [currentFile, setCurrentFile] = useState('');
  const [completedCount, setCompletedCount] = useState(0);

  // 通知书附件选择
  const [noticeAttachments, setNoticeAttachments] = useState([]);
  const [selectedAttachmentIds, setSelectedAttachmentIds] = useState([]);
  const [savingAttachments, setSavingAttachments] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  // 新附件上传
  const [attachUploadVisible, setAttachUploadVisible] = useState(false);
  const [attachUploading, setAttachUploading] = useState(false);
  const [attachUploadForm] = Form.useForm();

  useEffect(() => {
    loadNoticeAttachments();
  }, []);

  useEffect(() => {
    if (tourNotice) {
      setSelectedAttachmentIds(tourNotice.attachmentIds || []);
    }
  }, [tourNotice]);

  const loadNoticeAttachments = async () => {
    try {
      const res = await axios.get(`${API_BASE}/notice-attachments`, {
        headers: AuthService.getAuthHeaders()
      });
      if (res.data.success) {
        setNoticeAttachments(res.data.data.filter(a => a.isActive !== false));
      }
    } catch (err) {
      console.error('加载附件失败:', err);
    }
  };

  const handleSaveAttachments = async () => {
    try {
      setSavingAttachments(true);
      await axios.put(
        `${API_BASE}/tour-notices/tour/${tour.id}`,
        { attachmentIds: selectedAttachmentIds },
        { headers: AuthService.getAuthHeaders() }
      );
      message.success('附件选择已保存');
      onRefresh();
    } catch (err) {
      message.error('保存失败');
    } finally {
      setSavingAttachments(false);
    }
  };

  const getAttachmentUrl = (attachment) => {
    if (attachment.signedUrl) return attachment.signedUrl;
    if (attachment.filePath?.startsWith('/uploads/')) return `${API_BASE}${attachment.filePath}`;
    return attachment.filePath;
  };

  // 预览附件：图片弹图，PDF 新标签页打开
  const handlePreviewAttachment = (attachment) => {
    const url = getAttachmentUrl(attachment);
    if (!url) {
      message.error('附件地址无效');
      return;
    }
    if (attachment.fileType === 'image') {
      setPreviewImage(url);
    } else {
      window.open(url, '_blank');
    }
  };

  // 上传新附件到附件库
  const handleAttachUpload = async () => {
    let values;
    try {
      values = await attachUploadForm.validateFields();
    } catch (e) {
      return;
    }
    if (!values.file || values.file.length === 0) {
      message.error('请选择文件');
      return;
    }
    try {
      setAttachUploading(true);
      const formData = new FormData();
      formData.append('file', values.file[0].originFileObj);
      formData.append('name', values.name);
      const res = await axios.post(`${API_BASE}/notice-attachments/upload`, formData, {
        headers: { ...AuthService.getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      message.success('上传成功');
      setAttachUploadVisible(false);
      attachUploadForm.resetFields();
      await loadNoticeAttachments();
      // 新上传的附件自动勾选
      const newId = res.data?.data?.id;
      if (newId) setSelectedAttachmentIds(prev => [...prev, newId]);
    } catch (err) {
      message.error('上传失败: ' + (err.response?.data?.error || err.message));
    } finally {
      setAttachUploading(false);
    }
  };

  const touristDetails = completeness?.tourists || [];
  const stats = completeness?.tour?.touristStats;

  const handleUpload = async () => {
    if (files.length === 0) {
      message.error('请选择要上传的附件文件');
      return;
    }

    setUploading(true);
    setResult(null);
    setProgress(0);
    setCurrentFile('');
    setCompletedCount(0);

    const totalFiles = files.length;
    const results = { success: [], notMatched: [], errors: [] };
    const departureDate = tour?.departureDate ? dayjs(tour.departureDate).format('YYYY-MM-DD') : '';

    try {
      for (let i = 0; i < totalFiles; i++) {
        const file = files[i];
        setCurrentFile(file.name);
        setProgress(Math.round((i / totalFiles) * 100));

        const formData = new FormData();
        formData.append('departureDate', departureDate);
        formData.append('files', file);
        if (!matchName) {
          formData.append('matchMode', 'all');
        }

        try {
          const response = await axios.post(
            `${API_BASE}/tourist-documents/batch-upload-attachments`,
            formData,
            {
              headers: {
                ...AuthService.getAuthHeaders(),
                'Content-Type': 'multipart/form-data'
              }
            }
          );

          if (response.data.success) {
            const data = response.data.data;
            if (data.success) results.success.push(...data.success);
            if (data.notMatched) results.notMatched.push(...data.notMatched);
            if (data.errors) results.errors.push(...data.errors);
          }
        } catch (err) {
          results.errors.push({
            fileName: file.name,
            error: err.response?.data?.error || '上传失败'
          });
        }

        setCompletedCount(i + 1);
      }

      setProgress(100);
      setResult(results);

      const successCount = results.success.length;
      const notMatchedCount = results.notMatched.length;
      const errorCount = results.errors.length;
      message.success(`上传完成: ${successCount} 成功, ${notMatchedCount} 未匹配, ${errorCount} 失败`);

      onRefresh();
    } catch (error) {
      message.error(error.response?.data?.error || '批量上传失败');
    } finally {
      setUploading(false);
      setProgress(0);
      setCurrentFile('');
      setCompletedCount(0);
    }
  };

  const handleFileChange = (info) => {
    const fileList = info.fileList
      .filter(file => file.originFileObj && file.type === 'application/pdf')
      .map(file => file.originFileObj);
    setFiles(fileList);
  };

  const handleUploadSingle = async (touristId, file) => {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('documentType', 'attachment');
    formData.append('documentName', file.name);

    try {
      await axios.post(
        `${API_BASE}/tourist-documents/tourist/${touristId}`,
        formData,
        {
          headers: {
            ...AuthService.getAuthHeaders(),
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      message.success('上传成功');
      onRefresh();
    } catch (err) {
      message.error('上传失败: ' + (err.response?.data?.error || err.message));
    }
    return false;
  };

  const handleViewAttachment = async (documentId) => {
    try {
      const res = await axios.get(`${API_BASE}/tourist-documents/${documentId}/url`, {
        headers: AuthService.getAuthHeaders()
      });
      if (res.data.success && res.data.data?.fileUrl) {
        window.open(res.data.data.fileUrl, '_blank');
      } else {
        message.error('获取文件地址失败');
      }
    } catch (err) {
      message.error('获取文件地址失败');
    }
  };

  const handleDeleteAttachment = async (docId) => {
    try {
      await axios.delete(`${API_BASE}/tourist-documents/${docId}`, {
        headers: AuthService.getAuthHeaders()
      });
      message.success('已删除');
      onRefresh();
    } catch (err) {
      message.error('删除失败');
    }
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setFiles([]);
    setResult(null);
    setMatchName(true);
    onRefresh();
  };

  const columns = [
    {
      title: '护照姓名',
      dataIndex: 'passportName',
      key: 'passportName',
      width: 180,
      render: (val) => val || '-',
    },
    {
      title: '规格',
      key: 'spec',
      width: 120,
      render: (_, r) => {
        const P = { with_tour: ['Plus款', 'blue'], no_tour: ['基础款', 'default'] }[r.packageType];
        const T = { '2star': ['二钻', 'green'], '3star': ['三钻', 'gold'], '4star': ['四钻', 'volcano'], '5star': ['五钻', 'red'] }[r.hotelTier];
        const tags = [];
        if (P) tags.push(<Tag key="p" color={P[1]} style={{ margin: 0 }}>{P[0]}</Tag>);
        if (T) tags.push(<Tag key="t" color={T[1]} style={{ margin: 0 }}>{T[0]}</Tag>);
        if (!tags.length) return <span style={{ color: '#bbb', fontSize: 12 }}>默认规格</span>;
        return <Space size={4} wrap>{tags}</Space>;
      },
    },
    {
      title: '护照号',
      dataIndex: 'passportNumber',
      key: 'passportNumber',
      width: 120,
      render: (val) => val || '-',
    },
    {
      title: '附件PDF',
      key: 'attachment',
      width: 240,
      render: (_, record) => {
        const docs = record.attachmentDocuments || [];
        return (
          <div>
            {docs.length > 0 ? (
              <div style={{ marginBottom: 4 }}>
                {docs.map((doc) => (
                  <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2, fontSize: 12 }}>
                    <FilePdfOutlined style={{ color: '#ff4d4f', flexShrink: 0 }} />
                    <span
                      style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#1890ff', cursor: 'pointer', maxWidth: 140 }}
                      title={doc.documentName}
                      onClick={() => handleViewAttachment(doc.id)}
                    >
                      {doc.documentName}
                    </span>
                    <Button type="link" size="small" icon={<EyeOutlined />} style={{ padding: 0, height: 'auto', minWidth: 0 }} onClick={() => handleViewAttachment(doc.id)} />
                    <Button type="link" size="small" danger icon={<DeleteOutlined />} style={{ padding: 0, height: 'auto', minWidth: 0 }} onClick={() => handleDeleteAttachment(doc.id)} />
                  </div>
                ))}
              </div>
            ) : (
              <Tag color="default" style={{ marginBottom: 4 }}>未上传</Tag>
            )}
            <Upload
              beforeUpload={(file) => handleUploadSingle(record.id, file)}
              showUploadList={false}
              accept=".pdf"
              multiple
            >
              <Button size="small" icon={<UploadOutlined />} type={docs.length === 0 ? 'primary' : 'default'}>
                {docs.length === 0 ? '上传' : '追加'}
              </Button>
            </Upload>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      {/* 通知书附件选择 */}
      <Card
        title="通知书附件"
        size="small"
        style={{ marginBottom: 16 }}
        extra={
          <Space>
            <Button
              size="small"
              icon={<UploadOutlined />}
              onClick={() => setAttachUploadVisible(true)}
            >
              上传附件
            </Button>
            <Button
              type="primary"
              size="small"
              icon={<SaveOutlined />}
              loading={savingAttachments}
              onClick={handleSaveAttachments}
            >
              保存
            </Button>
          </Space>
        }
      >
        <p style={{ color: '#999', marginBottom: 12, fontSize: 13 }}>
          勾选的附件将展示在出团通知书中（如入境卡填写指南、海关申报说明等）
        </p>
        {noticeAttachments.length === 0 ? (
          <Empty description="暂无可用附件，点击右上角「上传附件」" />
        ) : (
          <Checkbox.Group
            value={selectedAttachmentIds}
            onChange={setSelectedAttachmentIds}
            style={{ width: '100%', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '8px 16px' }}
          >
            {noticeAttachments.map(a => (
              <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                <Checkbox value={a.id} style={{ minWidth: 0, flex: 1 }}>
                  <Space size={4} style={{ minWidth: 0 }}>
                    {a.fileType === 'image' ? <FileImageOutlined style={{ color: '#1890ff' }} /> : <FilePdfOutlined style={{ color: '#ff4d4f' }} />}
                    <span
                      style={{ maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', verticalAlign: 'bottom' }}
                      title={a.name}
                    >
                      {a.name}
                    </span>
                  </Space>
                </Checkbox>
                <span
                  style={{ color: '#1890ff', cursor: 'pointer', fontSize: 12, flexShrink: 0 }}
                  onClick={() => handlePreviewAttachment(a)}
                >
                  {a.fileType === 'image' ? '预览' : '打开'}
                </span>
              </div>
            ))}
          </Checkbox.Group>
        )}
      </Card>

      {/* 上传新附件 */}
      <Modal
        title="上传通知书附件"
        open={attachUploadVisible}
        onCancel={() => setAttachUploadVisible(false)}
        onOk={handleAttachUpload}
        confirmLoading={attachUploading}
        okText="上传"
      >
        <Form form={attachUploadForm} layout="vertical">
          <Form.Item name="name" label="附件名称" rules={[{ required: true, message: '请输入附件名称' }]}>
            <Input placeholder="如：中国入境网上填写指南" />
          </Form.Item>
          <Form.Item
            name="file"
            label="文件（PDF 或图片）"
            valuePropName="fileList"
            getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
            rules={[{ required: true, message: '请选择文件' }]}
          >
            <Upload.Dragger
              accept=".pdf,image/*"
              maxCount={1}
              beforeUpload={() => false}
            >
              <p className="ant-upload-drag-icon"><InboxOutlined /></p>
              <p className="ant-upload-text">点击或拖拽文件到此区域</p>
              <p className="ant-upload-hint">支持 PDF 和图片，上传后自动勾选</p>
            </Upload.Dragger>
          </Form.Item>
        </Form>
      </Modal>

      {/* 图片预览 */}
      <Image
        style={{ display: 'none' }}
        src={previewImage}
        preview={{
          visible: !!previewImage,
          src: previewImage,
          onVisibleChange: (visible) => { if (!visible) setPreviewImage(null); }
        }}
      />

      {/* 游客个人附件上传 */}
      <div className="stat-cards">
        <div className={`stat-card ${stats?.attachmentUploaded === stats?.total ? 'success' : 'warning'}`}>
          <div className="stat-value">{stats?.attachmentUploaded || 0}/{stats?.total || 0}</div>
          <div className="stat-label">已上传附件</div>
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PaperClipOutlined />}
          onClick={() => setModalVisible(true)}
        >
          批量匹配附件
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={touristDetails}
        rowKey="id"
        size="small"
        pagination={false}
      />

      <Modal
        title="批量匹配附件"
        open={modalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={640}
        destroyOnClose
      >
        {uploading ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <div style={{ marginBottom: 8, fontSize: 16, fontWeight: 500 }}>
              正在上传附件文件 ({completedCount}/{files.length})
            </div>
            <Progress
              percent={progress}
              status="active"
              strokeColor={{ '0%': '#108ee9', '100%': '#87d068' }}
              style={{ maxWidth: 400, margin: '0 auto' }}
            />
            {currentFile && (
              <div style={{ marginTop: 12, color: '#1890ff', fontFamily: 'monospace', fontSize: 13 }}>
                {currentFile}
              </div>
            )}
            <div style={{ marginTop: 12, color: '#999', fontSize: 12 }}>
              请勿关闭此窗口，正在逐个上传并匹配游客...
            </div>
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
              <span>匹配姓名：</span>
              <Switch checked={matchName} onChange={setMatchName} />
              <span style={{ color: '#999', fontSize: 12 }}>
                {matchName ? '根据文件名匹配游客' : '不匹配，直接发给所有游客'}
              </span>
            </div>
            {matchName && (
              <div style={{ marginBottom: 16, padding: '8px 12px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 4, fontSize: 13 }}>
                系统将从文件名中提取游客姓名进行匹配。文件名格式示例：<br/>
                <code>Confirmed Tour Itinerary & Hotels-CHENJINLAN & LIUTINGGONG.pdf</code><br/>
                文件名中最后一个 <b>-</b> 之后的部分为游客姓名，<b>&</b> 分隔多个游客。
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <Upload.Dragger
                accept=".pdf"
                multiple
                beforeUpload={() => false}
                onChange={handleFileChange}
                fileList={files.map((file, index) => ({
                  uid: index,
                  name: file.name,
                  status: 'done',
                  originFileObj: file,
                  type: file.type
                }))}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">点击或拖拽PDF文件到此区域</p>
                <p className="ant-upload-hint">
                  支持多文件上传，系统将从文件名自动匹配游客
                </p>
              </Upload.Dragger>
            </div>

            {files.length > 0 && (
              <div style={{ color: '#1890ff', marginBottom: 16 }}>
                已选择 {files.length} 个文件
              </div>
            )}

            <div style={{ textAlign: 'right', marginBottom: result ? 16 : 0 }}>
              <Space>
                <Button onClick={handleModalClose}>关闭</Button>
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  onClick={handleUpload}
                  disabled={files.length === 0}
                >
                  开始上传
                </Button>
              </Space>
            </div>

            {result && (
              <div>
                <div style={{ marginBottom: 12 }}>
                  <Space size="large">
                    <Tag color="success" style={{ padding: '4px 12px', fontSize: 14 }}>
                      <CheckCircleOutlined /> 成功: {result.success?.length || 0}
                    </Tag>
                    <Tag color="warning" style={{ padding: '4px 12px', fontSize: 14 }}>
                      <WarningOutlined /> 未匹配: {result.notMatched?.length || 0}
                    </Tag>
                    <Tag color="error" style={{ padding: '4px 12px', fontSize: 14 }}>
                      <ExclamationCircleOutlined /> 失败: {result.errors?.length || 0}
                    </Tag>
                  </Space>
                </div>

                {result.success?.length > 0 && (
                  <Card size="small" title="上传成功" style={{ marginBottom: 8 }}>
                    <List
                      size="small"
                      dataSource={result.success}
                      renderItem={(item) => (
                        <List.Item>
                          <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                          <span>{item.fileName}</span>
                          <span style={{ marginLeft: 8, color: '#666' }}>&rarr; {item.touristName}</span>
                        </List.Item>
                      )}
                      style={{ maxHeight: 150, overflow: 'auto' }}
                    />
                  </Card>
                )}

                {result.notMatched?.length > 0 && (
                  <Card size="small" title="未匹配游客" style={{ marginBottom: 8 }}>
                    <List
                      size="small"
                      dataSource={result.notMatched}
                      renderItem={(item) => (
                        <List.Item>
                          <WarningOutlined style={{ color: '#faad14', marginRight: 8 }} />
                          <span>{item.fileName}</span>
                          <span style={{ marginLeft: 8, color: '#999' }}>
                            {item.extractedNames && `(提取: ${Array.isArray(item.extractedNames) ? item.extractedNames.join(', ') : item.extractedNames})`}
                            {' '}{item.reason}
                          </span>
                        </List.Item>
                      )}
                      style={{ maxHeight: 120, overflow: 'auto' }}
                    />
                  </Card>
                )}

                {result.errors?.length > 0 && (
                  <Card size="small" title="上传失败" style={{ marginBottom: 8 }}>
                    <List
                      size="small"
                      dataSource={result.errors}
                      renderItem={(item) => (
                        <List.Item>
                          <ExclamationCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
                          <span>{item.fileName}</span>
                          <span style={{ marginLeft: 8, color: '#ff4d4f' }}>{item.error}</span>
                        </List.Item>
                      )}
                      style={{ maxHeight: 100, overflow: 'auto' }}
                    />
                  </Card>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default StepAttachmentUpload;
